import "./TextInput.css";

import { ReactNode } from "react";

import { FormLabel } from "../../FormLabel";
import { useTextInput } from "./useTextInput";

export type InputType = "text" | "email" | "number" | "date";

export interface TextInputProps {
  type?: InputType;
  label?: string;
  allowLabelDangerousHtml?: boolean;
  name: string;
  value?: string | null;
  placeholder?: string;
  helperText?: string;
  optional?: boolean;
  autoCompleteOff?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  onChange?: (value: string) => void;
  tooltip?: ReactNode;
  tooltipJustify?: "left" | "right";
  decimalPlaces?: number;
  maxLength?: number;
  displayCharacterCount?: boolean;
  debounceMilliseconds?: number;
  minNumberValue?: number; // Minimum value for number input
  maxNumberValue?: number; // Maximum value for number input
  prefix?: string;
  suffix?: string;
  hasError?: boolean;
}

export const TextInput = ({
  type = "text",
  label,
  allowLabelDangerousHtml,
  name,
  value,
  placeholder,
  helperText,
  optional,
  disabled,
  icon,
  autoCompleteOff,
  onChange,
  tooltip,
  tooltipJustify = "right",
  decimalPlaces,
  maxLength,
  displayCharacterCount,
  debounceMilliseconds,
  minNumberValue,
  maxNumberValue, // Default values for number input
  prefix,
  suffix,
  hasError = false,
}: TextInputProps): JSX.Element => {
  const { currentValue, onInputChange, onInputBlur } = useTextInput(
    name,
    value,
    type,
    decimalPlaces,
    onChange,
    debounceMilliseconds
  );

  return (
    <div
      className={`form_field TextInput ${disabled ? `TextInput_disabled` : ""} ${hasError ? "TextInput_hasError" : ""}`}
    >
      <FormLabel
        label={label}
        optional={optional}
        tooltip={tooltip}
        tooltipJustify={tooltipJustify}
        allowDangerousHtml={allowLabelDangerousHtml}
      >
        <input
          className={`${prefix !== undefined ? "HasPrefix" : ""} ${suffix !== undefined ? "HasSuffix" : ""}`.trim()}
          type={type}
          name={name}
          id={name}
          onKeyDown={type === "date" ? (e) => e.preventDefault() : undefined}
          onPaste={type === "date" ? (e) => e.preventDefault() : undefined}
          value={currentValue}
          placeholder={placeholder}
          disabled={disabled}
          onChange={onInputChange}
          onBlur={onInputBlur}
          autoComplete={autoCompleteOff ? "off" : "on"}
          step={decimalPlaces !== undefined ? 1 / 10 ** decimalPlaces : "any"}
          maxLength={maxLength}
          // Disable the number change on scrolling over input
          onWheel={(e) => {
            if (type === "number") {
              e.currentTarget.blur();
            }
          }}
          min={minNumberValue ?? undefined}
          max={maxNumberValue ?? undefined}
        />
        {prefix !== undefined && <span className="body1 Prefix">{prefix}</span>}
        {suffix !== undefined && <span className="body1 Suffix">{suffix}</span>}
        {displayCharacterCount && (
          <div className="body2 TextAreaCharacterCount">
            {currentValue?.length || 0}/{maxLength}
          </div>
        )}
        {icon && <div className="TextInputIcon">{icon}</div>}
        {helperText && <span className="body2 Helper">{helperText}</span>}
      </FormLabel>
    </div>
  );
};
